<!-- src/components/admin/AdminActivityAdjustments.vue -->
<template>
  <avs-card>
    <!-- ===== HEADER / TOOLBAR ===== -->
    <template #title>
      <v-toolbar flat class="mb-4 bgTable">
        <!-- Statut -->
        <v-select
          class="mr-4"
          dense
          hide-details
          :items="statusOptions"
          item-text="text"
          item-value="value"
          v-model="status"
          :label="$t('Statut')"
          @change="fetchReset"
        />

        <!-- Recherche globale -->
        <v-text-field
          class="mr-4"
          dense
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
          @input="debouncedSync"
        />

        <!-- Filtre Stations -->
        <v-autocomplete
          v-model="selectedShop"
          clearable
          :items="shops"
          @input="fetchReset"
          item-text="name"
          item-value="id"
          :label="$t('Stations')"
          dense
          hide-details
          prepend-inner-icon="mdi-map-marker"
          class="mr-4"
        />

        <v-spacer />

        <!-- Export Excel -->
        <v-btn
          color="success"
          :loading="exportingExcel"
          :disabled="exportingExcel || !items.length"
          @click="exportExcel"
        >
          <v-icon left>mdi-file-excel</v-icon>
          {{ $t('Export Excel') }}
        </v-btn>
      </v-toolbar>
    </template>

    <!-- ===== CONTENT ===== -->
    <template #content>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading && page === 1"
        :items-per-page="-1"
        :custom-sort="customSort"
        :must-sort="false"
        dense
        hide-default-footer
        class="elevation-0 ma-6 bgTable"
      >
        <!-- Date de création -->
        <template #item.created_at="{ item }">
          {{ toDatetime(item.created_at) }}
        </template>

        <!-- Type d'action (Annulation / Modification) -->
        <template #item.action="{ item }">
          <v-chip
            small
            label
            :color="item.action === 'cancel' ? 'error' : 'warning'"
            text-color="white"
          >
            {{ item.action === 'cancel' ? $t('Annulation') : $t('Modification') }}
          </v-chip>
        </template>

        <!-- Statut de la demande -->
        <template #item.status="{ item }">
          <v-chip
            small
            label
            :color="statusColor(item.status)"
            text-color="white"
          >
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <!-- Activité liée -->
        <template #item.activity="{ item }">
          <div>
            <div class="font-weight-medium">
              {{ item.activity?.transaction_number || '—' }}
            </div>
            <div class="caption">
              {{ getUserOrDriverName(item.activity) }}
            </div>
          </div>
        </template>

        <!-- Station -->
        <template #item.station="{ item }">
          {{ item.activity?.shop || '—' }}
        </template>

        <!-- Date de transaction -->
        <template #item.activity_date="{ item }">
          {{ item.activity?.created_at ? toDatetime(item.activity.created_at) : '—' }}
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <v-btn
            small
            outlined
            color="primary"
            @click="openDecisionDialog(item)"
          >
            <v-icon left small>mdi-eye</v-icon>
            {{ item.status === 'pending' ? $t('Statuer') : $t('Voir') }}
          </v-btn>
        </template>
      </v-data-table>

      <!-- Loader scroll infini -->
      <div
        class="py-4 d-flex justify-center"
        v-if="loadingMore"
      >
        <v-progress-circular indeterminate />
      </div>

      <!-- Fin de liste -->
      <div
        v-if="!loading && !loadingMore && !hasMore && items.length"
        class="text-center grey--text py-4"
      >
        {{ $t('Fin de la liste') }}
      </div>

      <!-- Sentinelle pour IntersectionObserver -->
      <div
        ref="sentinel"
        style="height:1px;"
      ></div>

      <!-- ===== MODALE DE DÉCISION ===== -->
      <v-dialog
        v-model="decisionDialog"
        max-width="960px"
        :fullscreen="$vuetify.breakpoint.xs"
        transition="dialog-bottom-transition"
      >
        <v-card v-if="selectedAdjustment">
          <!-- Header -->
          <v-card-title class="d-flex align-center">
            <v-icon left color="primary">mdi-tune-variant</v-icon>
            <div class="mr-4">
              {{ $t('Demande de régularisation') }}
              <div class="text-caption grey--text">
                #{{ selectedAdjustment.id }} •
                {{ toDatetime(selectedAdjustment.created_at) }}
              </div>
            </div>

            <v-spacer />

            <v-chip
              small
              class="mr-2"
              :color="statusColor(selectedAdjustment.status)"
              text-color="white"
            >
              {{ statusLabel(selectedAdjustment.status) }}
            </v-chip>

            <v-chip
              small
              :color="selectedAdjustment.action === 'cancel' ? 'error' : 'warning'"
              text-color="white"
            >
              {{ selectedAdjustment.action === 'cancel' ? $t('Annulation') : $t('Modification') }}
            </v-chip>
          </v-card-title>

          <v-divider />

          <!-- Contenu -->
          <v-card-text class="pa-4">
            <v-row dense>
              <!-- Résumé activité -->
              <v-col cols="12" md="6">
                <v-card outlined class="mb-3 adjust-summary-card">
                  <v-card-title class="subtitle-2 py-2">
                    <v-icon small left color="primary">mdi-gas-station</v-icon>
                    {{ $t("Activité d'origine") }}
                  </v-card-title>
                  <v-divider />
                  <v-card-text class="py-3">
                    <v-row dense>
                      <v-col cols="12">
                        <div class="summary-label">
                          {{ $t('Transaction #') }}
                        </div>
                        <div class="summary-value">
                          {{ selectedAdjustment.activity?.transaction_number || '—' }}
                        </div>
                      </v-col>

                      <v-col cols="12" sm="6">
                        <div class="summary-label">
                          {{ $t('Montant') }}
                        </div>
                        <div class="summary-value">
                          {{ formatNumber(selectedAdjustment.activity?.amount) }} XOF
                        </div>
                      </v-col>

                      <v-col cols="12" sm="6">
                        <div class="summary-label">
                          {{ $t('Quantité (L)') }}
                        </div>
                        <div class="summary-value">
                          {{ formatNumber(selectedAdjustment.activity?.quantity) }} L
                        </div>
                      </v-col>

                      <v-col cols="12" sm="6">
                        <div class="summary-label">
                          {{ $t('Produit') }}
                        </div>
                        <div class="summary-value">
                          {{ selectedAdjustment.activity?.product || 'N/A' }}
                        </div>
                      </v-col>

                      <!-- Client / Chauffeur + contact + société (comme AdminActivities) -->
                      <v-col cols="12">
                        <div class="summary-label">
                          {{ $t('Client / Chauffeur') }}
                        </div>
                        <div class="summary-value">
                          <div>
                            {{ getUserOrDriverName(selectedAdjustment.activity) }}
                          </div>
                          <div class="caption grey--text text--darken-1">
                            {{ getUserOrVehicleContact(selectedAdjustment.activity) }}
                            •
                            {{ getComanyName(selectedAdjustment.activity) }}
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="12">
                        <div class="summary-label">
                          {{ $t('Statut fraude') }}
                        </div>
                        <div class="summary-value">
                          <v-chip
                            x-small
                            :color="fraudColor(selectedAdjustment.activity)"
                            text-color="white"
                          >
                            {{ fraudLabel(selectedAdjustment.activity) }}
                          </v-chip>
                          <span
                            v-if="selectedAdjustment.activity?.is_cancelled"
                            class="ml-2 error--text text--darken-1 text-caption"
                          >
                            ({{ $t('Activité annulée') }})
                          </span>
                        </div>
                      </v-col>
                    </v-row>

                    <!-- Bouton voir les photos -->
                    <div class="mt-3 d-flex">
                      <v-btn
                        small
                        outlined
                        color="primary"
                        @click="photoDialog = true"
                      >
                        <v-icon left small>mdi-image-search-outline</v-icon>
                        {{ $t('Voir les photos') }}
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Résumé de la demande -->
              <v-col cols="12" md="6">
                <v-card outlined class="mb-3 adjust-summary-card">
                  <v-card-title class="subtitle-2 py-2">
                    <v-icon small left color="primary">mdi-file-document-edit</v-icon>
                    {{ $t('Demande de régularisation') }}
                  </v-card-title>
                  <v-divider />
                  <v-card-text class="py-3">
                    <v-row dense>
                      <v-col cols="12">
                        <div class="summary-label">
                          {{ $t('Action demandée') }}
                        </div>
                        <div class="summary-value">
                          {{ selectedAdjustment.action === 'cancel'
                            ? $t('Annulation de la transaction')
                            : $t('Modification des valeurs') }}
                        </div>
                      </v-col>

                      <v-col cols="12">
                        <div class="summary-label">
                          {{ $t('Motif') }}
                        </div>
                        <div class="summary-value">
                          {{ selectedAdjustment.reason || '—' }}
                        </div>
                      </v-col>

                      <!-- Comparaison montant -->
                      <v-col
                        cols="12"
                        sm="6"
                        v-if="selectedAdjustment.action === 'modify'"
                      >
                        <div class="summary-label">
                          {{ $t('Montant (avant → après)') }}
                        </div>
                        <div class="summary-value">
                          {{ formatNumber(selectedAdjustment.activity?.amount) }} XOF
                          <span v-if="selectedAdjustment.new_amount">
                            &nbsp;→&nbsp;
                            <strong>
                              {{ formatNumber(selectedAdjustment.new_amount) }} XOF
                            </strong>
                          </span>
                        </div>
                      </v-col>

                      <!-- Comparaison quantité -->
                      <v-col
                        cols="12"
                        sm="6"
                        v-if="selectedAdjustment.action === 'modify'"
                      >
                        <div class="summary-label">
                          {{ $t('Quantité (avant → après)') }}
                        </div>
                        <div class="summary-value">
                          {{ formatNumber(selectedAdjustment.activity?.quantity) }} L
                          <span v-if="selectedAdjustment.new_quantity">
                            &nbsp;→&nbsp;
                            <strong>
                              {{ formatNumber(selectedAdjustment.new_quantity) }} L
                            </strong>
                          </span>
                        </div>
                      </v-col>

                      <!-- Nouveau produit -->
                      <v-col
                        cols="12"
                        v-if="selectedAdjustment.action === 'modify' && selectedAdjustment.new_product"
                      >
                        <div class="summary-label">
                          {{ $t('Nouveau produit') }}
                        </div>
                        <div class="summary-value">
                          {{ selectedAdjustment.new_product_name || selectedAdjustment.new_product }}
                        </div>
                      </v-col>

                      <!-- Infos d'approbation -->
                      <v-col
                        cols="12"
                        v-if="selectedAdjustment.status !== 'pending'"
                      >
                        <div class="summary-label">
                          {{ $t("Décision prise par") }}
                        </div>
                        <div class="summary-value">
                          {{ selectedAdjustment.approved_by || '—' }}
                          <span
                            v-if="selectedAdjustment.approved_at"
                            class="grey--text text--darken-1 text-caption ml-1"
                          >
                            • {{ toDatetime(selectedAdjustment.approved_at) }}
                          </span>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider />

          <!-- Actions modale décision -->
          <v-card-actions class="pa-3">
            <v-spacer />
            <v-btn
              text
              @click="decisionDialog = false"
            >
              {{ $t('Fermer') }}
            </v-btn>
            <v-btn
              color="error"
              outlined
              :loading="decisionLoading && decisionType === 'reject'"
              :disabled="selectedAdjustment.status !== 'pending' || decisionLoading"
              class="ml-2"
              @click="handleDecision('reject')"
            >
              <v-icon left small>mdi-close-circle</v-icon>
              {{ $t('Rejeter') }}
            </v-btn>
            <v-btn
              color="success"
              dark
              :loading="decisionLoading && decisionType === 'approve'"
              :disabled="selectedAdjustment.status !== 'pending' || decisionLoading"
              class="ml-2"
              @click="handleDecision('approve')"
            >
              <v-icon left small>mdi-check-circle</v-icon>
              {{ $t('Approuver') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ===== MODALE PHOTOS ===== -->
      <v-dialog
        v-model="photoDialog"
        max-width="1000px"
        :fullscreen="$vuetify.breakpoint.xs"
      >
        <v-card v-if="selectedAdjustment">
          <v-card-title>
            <v-icon left color="primary">mdi-image-multiple</v-icon>
            {{ $t('Photos de la transaction') }}
            <v-spacer />
            <v-btn icon @click="photoDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="subtitle-2 py-2">
                    <v-icon small left color="primary">mdi-image</v-icon>
                    {{ $t('Photo de la pompe') }}
                  </v-card-title>
                  <v-divider />
                  <v-card-text class="pa-2">
                    <v-img
                      v-if="selectedAdjustment.activity?.image"
                      :src="selectedAdjustment.activity.image"
                      max-height="400"
                      contain
                      class="image-preview"
                    >
                      <template #placeholder>
                        <v-row
                          class="fill-height ma-0"
                          align="center"
                          justify="center"
                        >
                          <v-progress-circular indeterminate color="primary" />
                        </v-row>
                      </template>
                    </v-img>
                    <v-alert
                      v-else
                      type="warning"
                      dense
                      text
                    >
                      {{ $t('Image non disponible') }}
                    </v-alert>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="subtitle-2 py-2">
                    <v-icon small left color="primary">mdi-car</v-icon>
                    {{ $t('Photo du véhicule') }}
                  </v-card-title>
                  <v-divider />
                  <v-card-text class="pa-2">
                    <v-img
                      v-if="selectedAdjustment.activity?.vehicle_image"
                      :src="selectedAdjustment.activity.vehicle_image"
                      max-height="400"
                      contain
                      class="image-preview"
                    >
                      <template #placeholder>
                        <v-row
                          class="fill-height ma-0"
                          align="center"
                          justify="center"
                        >
                          <v-progress-circular indeterminate color="primary" />
                        </v-row>
                      </template>
                    </v-img>
                    <v-alert
                      v-else
                      type="info"
                      dense
                      text
                    >
                      {{ $t('Aucune photo véhicule') }}
                    </v-alert>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              @click="photoDialog = false"
            >
              {{ $t('Fermer') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import AdminServices from "@/services/AdminServices"
import { ShopDetailService } from "@/services"
import { toDatetime } from "@/utils/datetime"
import debounce from "lodash/debounce"

export default {
  name: "AdminActivityAdjustments",
  data() {
    return {
      search: "",
      status: "pending",
      statusOptions: [
        { text: this.$t("En attente"), value: "pending" },
        { text: this.$t("Approuvée"), value: "approved" },
        { text: this.$t("Rejetée"), value: "rejected" },
        { text: this.$t("Tous"), value: null },
      ],

      selectedShop: null,
      shops: [],

      loading: false,
      loadingMore: false,
      page: 1,
      items: [],
      hasMore: true,
      observer: null,
      exportingExcel: false,

      // Modale de décision
      decisionDialog: false,
      selectedAdjustment: null,
      decisionLoading: false,
      decisionType: null,

      // Modale photos
      photoDialog: false,

      headers: [
        { text: this.$t("Date de traitement"), value: "created_at", sortable: true },
        { text: this.$t("Action"), value: "action", sortable: true },
        { text: this.$t("Statut"), value: "status", sortable: true },
        { text: this.$t("Activité"), value: "activity", sortable: true },
        { text: this.$t("Station"), value: "station", sortable: true },
        { text: this.$t("Date de transaction"), value: "activity_date", sortable: true },
        { text: this.$t("Motif"), value: "reason", sortable: true },
        // Seule la colonne boutons n'est pas triable.
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    }
  },
  created() {
    this.debouncedSync = debounce(this.syncRoute, 250)
  },
  async mounted() {
    this.applyRouteFilters()
    await this.loadShops()
    this.fetchReset()
    this.$nextTick(this.createObserver)
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    toDatetime,

    // ===== Helpers statut demande =====
    statusColor(s) {
      return { pending: "warning", approved: "success", rejected: "error" }[s] || "grey"
    },
    statusLabel(s) {
      return (
        {
          pending: this.$t("En attente"),
          approved: this.$t("Approuvée"),
          rejected: this.$t("Rejetée"),
        }[s] || s
      )
    },

    // ===== Helpers activité (copié de AdminActivities) =====
    getUserOrVehicleContact(item) {
      if (!item) return "N/A"
      return item.vehicle
        ? item.vehicle.registration_number
        : (item.user?.phone_number || "N/A")
    },
    getUserOrDriverName(item) {
      if (!item) return "N/A"
      if (item.vehicle && item.vehicle.driver_name) {
        return `${item.vehicle.driver_name}`
      }
      if (item.user && item.user.first_name && item.user.last_name) {
        return `${item.user.first_name} ${item.user.last_name}`
      }
      return "N/A"
    },
    getComanyName(item) {
      if (!item) return ""
      if (item.vehicle) return item.vehicle.company
      if (item.user && item.user.phone_number === "+2250000000009") {
        return item.created_by?.company?.name || ""
      }
      return ""
    },

    fraudColor(activity) {
      const d = activity?.fraud_decision || "ok"
      return { ok: "success", suspect: "warning", rejected: "error" }[d] || "grey"
    },
    fraudLabel(activity) {
      const d = (activity?.fraud_decision || "ok").toLowerCase()
      if (d === "ok") return this.$t("OK")
      if (d === "suspect") return this.$t("Suspect")
      if (d === "rejected") return this.$t("Rejetée")
      return d
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "0"
      const num = typeof value === "number" ? value : parseFloat(value)
      if (isNaN(num)) return String(value)
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    },

    // ===== Filtres & URL =====
    applyRouteFilters() {
      const q = this.$route?.query || {}
      this.search = q.search || ""
      this.status = q.status || "pending"
      // selectedShop non persisté dans l'URL (comme AdminActivities)
    },
    syncRoute() {
      const q = {}
      if (this.search) q.search = this.search
      if (this.status) q.status = this.status
      this.$router.replace({ name: "AdminActivityAdjustments", query: q })
    },

    params() {
      const p = { page: this.page, ordering: "-created_at" }
      if (this.search) p.search = this.search
      if (this.status) p.status = this.status
      if (this.selectedShop) p.shop = this.selectedShop
      return p
    },

    // ===== Fetch / scroll infini =====
    async fetch({ reset = false } = {}) {
      if (reset) this.loading = true
      else this.loadingMore = true
      try {
        const data = await AdminServices.getActivityAdjustments(this, this.params())
        const results = Array.isArray(data) ? data : data.results || []
        this.items = reset ? results : this.items.concat(results)
        this.hasMore = !!(data && data.next)
      } finally {
        this.loading = false
        this.loadingMore = false
      }
      // Apres chaque fetch, on re-verifie si la sentinelle est encore
      // dans le viewport. Sinon l'IntersectionObserver ne se redeclenche
      // jamais (il ne fire que sur entree/sortie). Cas typique : la
      // sentinelle est deja visible apres le 1er chargement et on attend
      // un scroll qui n'arrive pas.
      this.$nextTick(() => this.maybeAutoLoadMore())
    },
    fetchReset() {
      this.page = 1
      this.hasMore = true
      this.items = []
      this.fetch({ reset: true })
    },

    // Tri client-side de toutes les colonnes (sauf actions). Gere les
    // valeurs imbriquees (ex. activity.shop) qui ne sont pas accessibles
    // par le sort standard de v-data-table via header.value.
    // Le tri s'applique sur les items deja charges (le scroll infini
    // continue d'apporter de nouveaux items qui rejoignent le tri).
    customSort(items, sortBy, sortDesc) {
      if (!sortBy || !sortBy.length) return items
      const key = sortBy[0]
      const desc = !!(sortDesc && sortDesc[0])

      const getValue = (it) => {
        switch (key) {
          case "created_at":
            return it.created_at ? new Date(it.created_at).getTime() : 0
          case "action":
            return (it.action || "").toLowerCase()
          case "status":
            return (it.status || "").toLowerCase()
          case "activity":
            return (it.activity?.transaction_number || "").toLowerCase()
          case "station":
            return (it.activity?.shop || "").toLowerCase()
          case "activity_date":
            return it.activity?.created_at ? new Date(it.activity.created_at).getTime() : 0
          case "reason":
            return (it.reason || "").toLowerCase()
          default:
            return it[key]
        }
      }

      return [...items].sort((a, b) => {
        const va = getValue(a)
        const vb = getValue(b)
        if (va < vb) return desc ? 1 : -1
        if (va > vb) return desc ? -1 : 1
        return 0
      })
    },

    createObserver() {
      const el = this.$refs.sentinel
      if (!el) return
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) this.loadMore()
        },
        { root: null, rootMargin: "200px", threshold: 0 }
      )
      this.observer.observe(el)
    },
    // Force un loadMore si la sentinelle est encore visible apres un fetch.
    maybeAutoLoadMore() {
      if (!this.hasMore || this.loading || this.loadingMore) return
      const el = this.$refs.sentinel
      if (!el) return
      const rect = el.getBoundingClientRect()
      // Si la sentinelle est dans le viewport (ou tres proche), on
      // continue a charger.
      if (rect.top < (window.innerHeight || document.documentElement.clientHeight) + 200) {
        this.loadMore()
      }
    },
    loadMore() {
      if (this.loading || this.loadingMore || !this.hasMore) return
      this.page += 1
      this.fetch()
    },

    // ===== Export Excel =====
    async exportExcel() {
      if (this.exportingExcel) return
      this.exportingExcel = true
      try {
        // On charge TOUTES les pages (boucle jusqu'a hasMore=false) pour
        // exporter l'integralite des resultats du filtre courant, pas seulement
        // ce qui est affiche.
        const collected = []
        let page = 1
        let safety = 0
        let next = true
        while (next && safety < 100) {
          const p = { ...this.params(), page }
          const data = await AdminServices.getActivityAdjustments(this, p)
          const results = Array.isArray(data) ? data : data.results || []
          collected.push(...results)
          next = !!(data && data.next)
          page += 1
          safety += 1
        }
        if (!collected.length) {
          this.$store.commit("app/message", {
            msg: this.$t("Aucune donnee a exporter"),
            color: "warning",
          })
          return
        }
        const XLSX = await import("xlsx")
        const { saveAs } = await import("file-saver")
        const header = [
          this.$t("Date de demande"),
          this.$t("Date d'approbation"),
          this.$t("Date d'application"),
          this.$t("Action"),
          this.$t("Statut"),
          this.$t("Transaction"),
          this.$t("Conducteur"),
          this.$t("Station"),
          this.$t("Date de transaction"),
          this.$t("Motif"),
          this.$t("Nouveau montant"),
          this.$t("Nouveau litrage"),
          this.$t("Ancien montant"),
          this.$t("Ancien litrage"),
        ]
        const rows = [header]
        collected.forEach(it => {
          // Litrage courant : on prend la quantite cible (new_quantity)
          // si la modification a renseigne cette valeur, sinon la
          // quantite actuelle de l'activity. Pour les annulations, on
          // affiche 0 (rien ne reste apres annulation).
          let litrage
          if (it.action === "cancel" && it.status === "approved") {
            litrage = 0
          } else if (it.new_quantity != null) {
            litrage = Number(it.new_quantity)
          } else {
            litrage = Number(it.activity?.quantity || 0)
          }
          let montant
          if (it.action === "cancel" && it.status === "approved") {
            montant = 0
          } else if (it.new_amount != null) {
            montant = Number(it.new_amount)
          } else {
            montant = Number(it.activity?.amount || 0)
          }
          // Resolution "Ancien montant / Ancien litrage" :
          //  1) Snapshot direct (original_amount / original_quantity ajoutes
          //     au modele, captures a la creation de la demande).
          //  2) PENDING : l'activity n'a pas encore ete modifiee -> ses
          //     valeurs courantes SONT les anciennes valeurs.
          //  3) MODIFY+APPROVED : on regarde axe par axe.
          //     - Si new_amount/new_quantity est renseigne, le champ a ete
          //       modifie -> on reconstruit via delta :
          //         old = act.amount/quantity (post-apply) + delta
          //         (cf. compute_deltas : delta = old - new)
          //     - Si new_amount/new_quantity est null, ce champ n'a PAS
          //       ete modifie -> ancien = courant = act.amount/quantity.
          //  4) Sinon : "—" (info perdue, cas CANCEL approved sans
          //     snapshot par exemple).
          let ancienMontant = null
          let ancienLitrage = null
          if (it.original_amount != null) {
            ancienMontant = Number(it.original_amount)
          } else if (it.status === "pending") {
            ancienMontant = Number(it.activity?.amount || 0)
          } else if (it.action === "modify" && it.status === "approved") {
            if (it.new_amount != null) {
              // Le montant a ete modifie : reconstruction via delta
              const deltaA = Number(it.deltas?.voucher?.amount || 0)
              ancienMontant = Number(it.activity?.amount || 0) + deltaA
            } else {
              // Le montant n'a pas ete modifie : ancien == courant
              ancienMontant = Number(it.activity?.amount || 0)
            }
          }
          if (it.original_quantity != null) {
            ancienLitrage = Number(it.original_quantity)
          } else if (it.status === "pending") {
            ancienLitrage = Number(it.activity?.quantity || 0)
          } else if (it.action === "modify" && it.status === "approved") {
            if (it.new_quantity != null) {
              // La quantite a ete modifiee : reconstruction via delta
              const deltaL = Number(it.deltas?.voucher?.liters || 0)
              ancienLitrage = Number(it.activity?.quantity || 0) + deltaL
            } else {
              // La quantite n'a pas ete modifiee : ancien == courant
              ancienLitrage = Number(it.activity?.quantity || 0)
            }
          }
          rows.push([
            toDatetime(it.created_at) || "",
            it.approved_at ? toDatetime(it.approved_at) : "",
            it.executed_at ? toDatetime(it.executed_at) : "",
            it.action === "cancel" ? "Annulation" : "Modification",
            this.statusLabel(it.status),
            it.activity?.transaction_number || "",
            this.getUserOrDriverName(it.activity),
            it.activity?.shop || "",
            it.activity?.created_at ? toDatetime(it.activity.created_at) : "",
            it.reason || "",
            Math.round(montant),
            Math.round(litrage * 100) / 100,
            ancienMontant != null ? Math.round(ancienMontant) : "—",
            ancienLitrage != null ? Math.round(ancienLitrage * 100) / 100 : "—",
          ])
        })
        const ws = XLSX.utils.aoa_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, "Approbations")
        const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
        const blob = new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
        saveAs(blob, `approbations_${new Date().toISOString().slice(0, 10)}.xlsx`)
        this.$store.commit("app/message", {
          msg: this.$t("Export reussi"),
          color: "success",
        })
      } catch (e) {
        console.error("Erreur export:", e)
        this.$store.commit("app/message", {
          msg: this.$t("Impossible d'exporter les donnees"),
          color: "error",
        })
      } finally {
        this.exportingExcel = false
      }
    },

    // ===== Chargement des stations =====
    async loadShops() {
      try {
        const r = await ShopDetailService.getAdminprincipalAll(this)
        this.shops = Array.isArray(r) ? r : (r?.results || [])
      } catch (e) {
        this.shops = []
      }
    },

    // ===== Modale décision =====
    openDecisionDialog(item) {
      this.selectedAdjustment = item
      this.decisionDialog = true
      this.photoDialog = false
      this.decisionLoading = false
      this.decisionType = null
    },

    async handleDecision(type) {
      if (!this.selectedAdjustment) return
      if (this.selectedAdjustment.status !== "pending") {
        this.$store.commit("app/message", {
          msg: this.$t("Cette demande n'est plus en attente."),
          color: "warning",
        })
        return
      }

      this.decisionType = type
      this.decisionLoading = true
      try {
        if (type === "approve") {
          await AdminServices.approveActivityAdjustment(this, this.selectedAdjustment.id)
          this.$store.commit("app/message", {
            msg: this.$t("Demande approuvée."),
            color: "success",
          })
        } else if (type === "reject") {
          await AdminServices.rejectActivityAdjustment(this, this.selectedAdjustment.id)
          this.$store.commit("app/message", {
            msg: this.$t("Demande rejetée."),
            color: "success",
          })
        }
        this.decisionDialog = false
        this.photoDialog = false
        this.fetchReset()
      } catch (e) {
        const serverMsg =
          e?.response?.data?.error ||
          e?.response?.data?.detail ||
          (typeof e?.response?.data === "string" ? e.response.data : null)
        this.$store.commit("app/message", {
          msg:
            serverMsg ||
            (type === "approve"
              ? this.$t("Échec de l'approbation.")
              : this.$t("Échec du rejet.")),
          color: "error",
        })
      } finally {
        this.decisionLoading = false
        this.decisionType = null
      }
    },
  },
}
</script>

<style scoped>
.adjust-summary-card {
  border-radius: 12px;
}

.summary-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #757575;
  font-weight: 600;
  margin-bottom: 2px;
}

.summary-value {
  font-size: 13px;
  font-weight: 700;
  color: #212121;
}

/* Image hover */
.image-preview {
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.image-preview:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
}
</style>
